import { createWebHistory, createRouter } from "vue-router";
import { useAuthStore, useNotificationStore } from "@/state/pinia";
import { canNavigate } from '@/libs/acl/routeProtection'
import adminRoute from './adminRoute'
import recruiterRoute from './recruiterRoute'
import influencerRoute from './influencerRoute'
import baseRoutes from "./routes";
import appConfig from "../app.config.json";

const currentUser = JSON.parse(localStorage.getItem('authUser')) || null
// Khởi tạo routes chung trước
let routes = [...baseRoutes];

// Nối thêm các route dựa trên quyền của user
if (currentUser && currentUser.role == 'ADMIN') {
  routes = [...routes, ...recruiterRoute];
  routes = [...routes, ...adminRoute];
}

if (currentUser && currentUser.role == 'REC') {
  routes = [...routes, ...recruiterRoute];
}
if (currentUser && currentUser.role == 'KOL') {
  routes = [...routes, ...influencerRoute];
}

const router = createRouter({
  history: createWebHistory(),
  routes,
  // Use the HTML5 history API (i.e. normal-looking routes)
  // instead of routes with hashes (e.g. example.com/#/about).
  // This may require some server configuration in production:
  // https://router.vuejs.org/en/essentials/history-mode.html#example-server-configurations
  mode: "history",
  // Simulate native-like scroll behavior when navigating to a new
  // route and using back/forward buttons.
  scrollBehavior(to, from, savedPosition) {
    if (savedPosition) {
      return savedPosition;
    } else {
      return { x: 0, y: 0 };
    }
  }
});

// Before each route evaluates...
router.beforeEach((routeTo, routeFrom, next) => {
  const auth = useAuthStore()
  const notificationStore = useNotificationStore();

  // Xác định các trang công khai không yêu cầu đăng nhập
  const publicPages = ["/login", "/register", "/forgot-password", "/reset-password"];
  const authRequired = !publicPages.includes(routeTo.path);

  // Nếu không cần đăng nhập (các trang công khai), cho phép truy cập
  if (!authRequired) {
    return next();
  }

  // Kiểm tra trạng thái đăng nhập từ auth store
  if (auth.loggedIn) {
    // Nếu đã đăng nhập và token hợp lệ, cho phép truy cập
    const validUser = auth.validate(); // Giả định validate trả về true/false
    if (validUser) {
      if (!canNavigate(routeTo)) {
        // Lưu trữ URL hiện tại
        localStorage.setItem('redirectAfterLogin', routeTo.fullPath)
        // If logged in => not authorized
        return next({ name: 'default' })
      }
      return next(); // Cho phép truy cập vào trang yêu cầu xác thực
    } else {
      // Token không hợp lệ hoặc hết hạn, yêu cầu đăng nhập lại
      notificationStore.error("Phiên đăng nhập của bạn đã hết hạn. Vui lòng đăng nhập lại.");
      return redirectToLogin();
    }
  } else {
    // Nếu người dùng chưa đăng nhập, yêu cầu đăng nhập
    return redirectToLogin();
  }

  // Hàm điều hướng đến trang login
  function redirectToLogin() {
    // Pass the original route to the login component để điều hướng sau khi đăng nhập thành công
    next({ name: "login", query: { redirectFrom: routeTo.fullPath } });
  }
});

router.beforeResolve(async (routeTo, routeFrom, next) => {
  // Create a `beforeResolve` hook, which fires whenever
  // `beforeRouteEnter` and `beforeRouteUpdate` would. This
  // allows us to ensure data is fetched even when params change,
  // but the resolved route does not. We put it in `meta` to
  // indicate that it's a hook we created, rather than part of
  // Vue Router (yet?).
  try {
    // For each matched route...
    for (const route of routeTo.matched) {
      await new Promise((resolve, reject) => {
        // If a `beforeResolve` hook is defined, call it with
        // the same arguments as the `beforeEnter` hook.
        if (route.meta && route.meta.beforeResolve) {
          route.meta.beforeResolve(routeTo, routeFrom, (...args) => {
            // If the user chose to redirect...
            if (args.length) {
              // If redirecting to the same route we're coming from...
              // Complete the redirect.
              next(...args);
              reject(new Error("Redirected"));
            } else {
              resolve();
            }
          });
        } else {
          // Otherwise, continue resolving the route.
          resolve();
        }
      });
    }
    // If a `beforeResolve` hook chose to redirect, just return.
  } catch (error) {
    return;
  }

  document.title = routeTo.meta.title + " | " + appConfig.title;
  // If we reach this point, continue resolving the route.
  next();
});

export default router;
